import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  Card, Form, Input, Select, Button, Space, message, Row, Col, Upload, Spin,
  Modal, Tag, Typography, Collapse
} from 'antd';
import {
  ArrowLeftOutlined, SaveOutlined,
  FileTextOutlined, PaperClipOutlined
} from '@ant-design/icons';
import axios from 'axios';

const { TextArea } = Input;
const { Option } = Select;
const { Text } = Typography;

const PRIORITY_COLOR = { CRITICAL: 'red', HIGH: 'orange', MEDIUM: 'blue', LOW: 'default' };

/**
 * A searchable picker over a paginated DRF list endpoint (`?search=`).
 * Loads the first page on open, searches as you type (debounced), and keeps
 * the chosen row's label even after the options change.
 */
const SearchSelect = ({ url, placeholder, toOption, value, onChange, onRowChange, id }) => {
  const [options, setOptions] = useState([]);
  const [rows, setRows] = useState({});
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const ticket = React.useRef(0);
  const timer = React.useRef(null);

  const load = (search) => {
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      const mine = ++ticket.current;
      setLoading(true);
      try {
        const response = await axios.get(url, { params: { search, page_size: 20 } });
        const list = response.data?.results || response.data || [];
        if (mine !== ticket.current) return;
        setRows(prev => ({ ...prev, ...Object.fromEntries(list.map(r => [r.id, r])) }));
        setOptions(list.map(toOption));
      } catch (error) {
        if (mine === ticket.current) setOptions([]);
      }
      if (mine === ticket.current) setLoading(false);
    }, search ? 300 : 0);
  };

  useEffect(() => () => clearTimeout(timer.current), []);

  // The chosen option stays visible even when a later search drops it.
  const shown = selected && !options.some(o => o.value === selected.value)
    ? [selected, ...options]
    : options;

  return (
    <Select
      id={id}
      showSearch
      allowClear
      value={value}
      placeholder={placeholder}
      filterOption={false}
      loading={loading}
      onSearch={load}
      onDropdownVisibleChange={(open) => { if (open && !options.length) load(''); }}
      notFoundContent={loading ? <Spin size="small" /> : 'No matches'}
      optionLabelProp="label"
      popupMatchSelectWidth={false}
      onChange={(next) => {
        const option = shown.find(o => o.value === next) || null;
        setSelected(option);
        onChange?.(next);
        onRowChange?.(next ? rows[next] || null : null);
      }}
    >
      {shown.map(option => (
        <Option key={option.value} value={option.value} label={option.label}>
          <div style={{ maxWidth: 420 }}>{option.render}</div>
        </Option>
      ))}
    </Select>
  );
};

const CreateTicket = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  // Project state
  // The selected project row (for its support components).
  const [projectInfo, setProjectInfo] = useState(null);

  // Attachments state
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);

  const ticketStatuses = useSelector(state => state.support.ticketStatuses);
  const ticketCategories = useSelector(state => state.support.ticketCategories);
  const ticketTemplates = useSelector(state => state.support.ticketTemplates);

  // Ticket template: prefills title, description, category and priority.
  const [templateId, setTemplateId] = useState(null);
  const [openSections, setOpenSections] = useState([]);
  const activeTemplates = (ticketTemplates || []).filter(t => t.is_active);
  const selectedTemplate = activeTemplates.find(t => t.id === templateId) || null;
  // Fields the template lists that exist on this form become required.
  const templateRequires = new Set(Object.keys(selectedTemplate?.required_fields || {}));
  const requiredRule = (name, label) => (
    templateRequires.has(name)
      ? [{ required: true, message: `${label} is required for "${selectedTemplate.name}"` }]
      : []
  );

  const applyTemplate = (id) => {
    const template = activeTemplates.find(t => t.id === id);
    if (!template) {
      setTemplateId(null);
      return;
    }
    const fill = () => {
      form.setFieldsValue({
        title: template.title_template,
        description: template.description_template,
        priority: template.default_priority || 'MEDIUM',
        ...(template.default_category ? { category: template.default_category } : {}),
      });
      setTemplateId(id);
    };
    // Don't silently throw away what the agent already typed.
    const { title, description } = form.getFieldsValue(['title', 'description']);
    const previous = activeTemplates.find(t => t.id === templateId);
    const edited = (title && title !== previous?.title_template)
      || (description && description !== previous?.description_template);
    if (!edited) {
      fill();
      return;
    }
    Modal.confirm({
      title: `Use the "${template.name}" template?`,
      content: 'This replaces the title and description you have entered.',
      okText: 'Replace',
      onOk: fill,
    });
  };

  // Environment detection
  const detectEnvironment = () => {
    const userAgent = navigator.userAgent;

    let os = 'Unknown';
    if (userAgent.indexOf('Win') !== -1) os = 'Windows';
    else if (userAgent.indexOf('Mac') !== -1) os = 'macOS';
    else if (userAgent.indexOf('Linux') !== -1) os = 'Linux';
    else if (userAgent.indexOf('Android') !== -1) os = 'Android';
    else if (userAgent.indexOf('iOS') !== -1 || userAgent.indexOf('iPhone') !== -1) os = 'iOS';

    let browser = 'Unknown';
    if (userAgent.indexOf('Chrome') !== -1 && userAgent.indexOf('Edg') === -1) browser = 'Chrome';
    else if (userAgent.indexOf('Safari') !== -1 && userAgent.indexOf('Chrome') === -1) browser = 'Safari';
    else if (userAgent.indexOf('Firefox') !== -1) browser = 'Firefox';
    else if (userAgent.indexOf('Edg') !== -1) browser = 'Edge';

    const isMobile = /Android|iPhone|iPad|iPod/i.test(userAgent);
    const device = isMobile ? 'Mobile' : 'Desktop';

    return { os, browser, device };
  };

  // Auto-detect environment on mount
  useEffect(() => {
    const env = detectEnvironment();
    form.setFieldsValue({
      environment_info: env
    });
  }, []);

  // Handle file upload
  const handleUpload = async (file) => {
    const formData = new FormData();
    formData.append('document_file', file);
    formData.append('name', file.name);
    formData.append('document_type', 'ATTACHMENT');

    setUploading(true);
    try {
      const response = await axios.post('/document/document/', formData);
      setAttachments(prev => [...prev, {
        id: response.data.id,
        name: file.name,
        size: file.size,
        uid: file.uid || response.data.id
      }]);
      message.success(`${file.name} uploaded`);
    } catch (error) {
      message.error(`Failed to upload ${file.name}`);
    } finally {
      setUploading(false);
    }
    return false; // Prevent default upload
  };

  const removeAttachment = (uid) => {
    setAttachments(prev => prev.filter(a => a.uid !== uid && a.id !== uid));
  };

  const handleSubmit = async (values) => {
    try {
      setLoading(true);

      const ticketData = {
        ...values,
        account: values.account || null,
        source: 'ADMIN_CREATED',
        attachments: attachments.map(a => a.id),
        environment_info: values.environment_info || detectEnvironment()
      };

      const response = await axios.post('support/tickets/', ticketData);
      message.success('Ticket created successfully');
      navigate(`/support/tickets/${response.data.id}`);
    } catch (error) {
      console.error('Failed to create ticket:', error);
      message.error(error.response?.data?.message || 'Failed to create ticket');
    } finally {
      setLoading(false);
    }
  };

  const defaultStatus = ticketStatuses.find(s => s.is_default);

  // initialValues are read once, usually before the statuses have loaded, so
  // apply the default status when it arrives unless one was already picked.
  useEffect(() => {
    if (defaultStatus && !form.getFieldValue('status')) {
      form.setFieldsValue({ status: defaultStatus.id });
    }
  }, [defaultStatus, form]);

  // Get components from project or use defaults
  const components = (projectInfo?.support_components?.length && projectInfo.support_components) || [
    'Frontend', 'Backend', 'API', 'Database', 'Mobile App', 'Documentation', 'DevOps', 'Other'
  ];

  // Optional sections live in a collapse; open the ones a template needs or
  // that fail validation, so a required field is never hidden.
  const SECTION_FIELDS = {
    bug: ['steps_to_reproduce', 'expected_result', 'actual_result', 'console_logs'],
    context: ['component', 'issue_url'],
    environment: ['environment_info'],
  };
  const sectionOf = (name) => Object.keys(SECTION_FIELDS)
    .find(key => SECTION_FIELDS[key].includes(Array.isArray(name) ? name[0] : name));

  useEffect(() => {
    const needed = [...templateRequires].map(sectionOf).filter(Boolean);
    if (needed.length) setOpenSections(prev => [...new Set([...prev, ...needed])]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateId]);

  const handleFinishFailed = ({ errorFields }) => {
    const sections = errorFields.map(f => sectionOf(f.name)).filter(Boolean);
    if (sections.length) setOpenSections(prev => [...new Set([...prev, ...sections])]);
  };

  const compact = { marginBottom: 12 };

  const moreSections = [
    {
      key: 'context',
      label: 'Component & issue URL',
      forceRender: true,
      children: (
        <Row gutter={12}>
          <Col xs={24} md={12}>
            <Form.Item name="component" label="Component" style={compact}>
              <Select placeholder="Select component" allowClear>
                {components.map(comp => (
                  <Option key={comp} value={comp}>{comp}</Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              name="issue_url"
              label="Issue URL"
              rules={requiredRule('issue_url', 'Issue URL')}
              style={compact}
            >
              <Input placeholder="Where the issue occurred" />
            </Form.Item>
          </Col>
        </Row>
      ),
    },
    {
      key: 'bug',
      label: 'Bug details',
      forceRender: true,
      children: (
        <>
          <Form.Item
            name="steps_to_reproduce"
            label="Steps to reproduce"
            rules={requiredRule('steps_to_reproduce', 'Steps to reproduce')}
            style={compact}
          >
            <TextArea autoSize={{ minRows: 2, maxRows: 8 }} placeholder={'1. Go to…\n2. Click on…\n3. Observe…'} />
          </Form.Item>
          <Row gutter={12}>
            <Col xs={24} md={12}>
              <Form.Item
                name="expected_result"
                label="Expected result"
                rules={requiredRule('expected_result', 'Expected result')}
                style={compact}
              >
                <TextArea autoSize={{ minRows: 2, maxRows: 6 }} placeholder="What should have happened?" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="actual_result"
                label="Actual result"
                rules={requiredRule('actual_result', 'Actual result')}
                style={compact}
              >
                <TextArea autoSize={{ minRows: 2, maxRows: 6 }} placeholder="What actually happened?" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="console_logs" label="Console logs / error messages" style={compact}>
            <TextArea
              autoSize={{ minRows: 2, maxRows: 8 }}
              placeholder="Paste error messages or console logs"
              style={{ fontFamily: 'monospace', fontSize: 12 }}
            />
          </Form.Item>
        </>
      ),
    },
    {
      key: 'environment',
      label: (
        <Space size={6}>
          Environment
          <Text type="secondary" style={{ fontSize: 12 }}>(auto-detected)</Text>
        </Space>
      ),
      forceRender: true,
      children: (
        <Row gutter={12}>
          <Col xs={24} md={8}>
            <Form.Item name={['environment_info', 'os']} label="Operating system" style={compact}>
              <Select options={['Windows', 'macOS', 'Linux', 'Android', 'iOS', 'Other'].map(v => ({ value: v, label: v }))} />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item name={['environment_info', 'browser']} label="Browser" style={compact}>
              <Select options={['Chrome', 'Firefox', 'Safari', 'Edge', 'Opera', 'Other'].map(v => ({ value: v, label: v }))} />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item name={['environment_info', 'device']} label="Device" style={compact}>
              <Select options={['Desktop', 'Mobile', 'Tablet'].map(v => ({ value: v, label: v }))} />
            </Form.Item>
          </Col>
        </Row>
      ),
    },
  ];

  return (
    <div style={{ padding: 16 }}>
      <Card
        size="small"
        style={{ maxWidth: 1100, margin: '0 auto' }}
        title={
          <Space>
            <Button
              type="text"
              size="small"
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate('/support/tickets')}
            />
            Create ticket
          </Space>
        }
        extra={
          <Space>
            <Button size="small" onClick={() => navigate('/support/tickets')}>Cancel</Button>
            <Button
              size="small"
              type="primary"
              icon={<SaveOutlined />}
              loading={loading}
              onClick={() => form.submit()}
            >
              Create ticket
            </Button>
          </Space>
        }
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          onFinishFailed={handleFinishFailed}
          requiredMark="optional"
          initialValues={{
            priority: 'MEDIUM',
            status: defaultStatus?.id,
          }}
        >
          <Row gutter={12}>
            <Col xs={24} md={8}>
              <Form.Item
                name="project"
                label="Project"
                style={compact}
                rules={[{ required: true, message: 'Please select a project' }]}
              >
                <SearchSelect
                  url="/project_board/projects/"
                  placeholder="Search project by name or code"
                  onRowChange={(row) => {
                    setProjectInfo(row);
                    form.setFieldsValue({ component: undefined });
                  }}
                  toOption={(project) => ({
                    value: project.id,
                    label: `${project.name} (${project.project_code})`,
                    render: (
                      <div>
                        <div style={{ fontWeight: 500 }}>{project.name}</div>
                        <div style={{ fontSize: 12, color: '#8c8c8c' }}>
                          {project.project_code}
                          {project.status_display ? ` · ${project.status_display}` : ''}
                        </div>
                      </div>
                    ),
                  })}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="account" label="Account" style={compact}>
                <SearchSelect
                  url="/accounts/account/"
                  placeholder="Search name, email, phone, GSTIN"
                  toOption={(account) => ({
                    value: account.id,
                    label: `${account.name}${account.account_number ? ` (${account.account_number})` : ''}`,
                    render: (
                      <div>
                        <div style={{ fontWeight: 500 }}>
                          {account.name}
                          {account.account_number && (
                            <Text type="secondary" style={{ fontSize: 12, marginLeft: 6 }}>
                              {account.account_number}
                            </Text>
                          )}
                        </div>
                        <div style={{ fontSize: 12, color: '#8c8c8c' }}>
                          {[account.email, account.phone].filter(Boolean).join(' · ') || '—'}
                        </div>
                      </div>
                    ),
                  })}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item label="Template" style={compact}>
                <Select
                  allowClear
                  showSearch
                  disabled={!activeTemplates.length}
                  placeholder={activeTemplates.length ? 'Start from a template' : 'No templates yet'}
                  value={templateId}
                  onChange={applyTemplate}
                  optionFilterProp="search"
                  optionLabelProp="label"
                  popupMatchSelectWidth={false}
                  listHeight={320}
                >
                  {activeTemplates.map(template => (
                    <Option
                      key={template.id}
                      value={template.id}
                      label={<Space size={6}><FileTextOutlined />{template.name}</Space>}
                      search={`${template.name} ${template.description || ''} ${template.default_category_name || ''}`}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, maxWidth: 520 }}>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontWeight: 500 }}>{template.name}</div>
                          {template.description && (
                            <div style={{ fontSize: 12, color: '#8c8c8c', whiteSpace: 'normal' }}>
                              {template.description}
                            </div>
                          )}
                        </div>
                        <Tag color={PRIORITY_COLOR[template.default_priority]} style={{ alignSelf: 'flex-start', margin: 0 }}>
                          {template.default_priority_display || template.default_priority}
                        </Tag>
                      </div>
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="title"
            label="Title"
            style={compact}
            rules={[
              { required: true, message: 'Please enter ticket title' },
              { max: 200, message: 'Title cannot exceed 200 characters' }
            ]}
            extra={selectedTemplate ? 'Replace the [bracketed] parts with the details.' : undefined}
          >
            <Input placeholder="Brief summary of the issue" />
          </Form.Item>

          <Row gutter={12}>
            <Col xs={24} md={10}>
              <Form.Item
                name="category"
                label="Category"
                style={compact}
                rules={[{ required: true, message: 'Please select a category' }]}
              >
                <Select placeholder="Select category" showSearch optionFilterProp="search">
                  {ticketCategories.map(cat => (
                    <Option key={cat.id} value={cat.id} search={cat.name}>
                      {cat.icon && <span style={{ marginRight: 8 }}>{cat.icon}</span>}
                      {cat.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={12} md={7}>
              <Form.Item
                name="priority"
                label="Priority"
                style={compact}
                rules={[{ required: true, message: 'Please select priority' }]}
              >
                <Select
                  options={['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'].map(value => ({
                    value,
                    label: <Tag color={PRIORITY_COLOR[value]} style={{ margin: 0 }}>{value.charAt(0) + value.slice(1).toLowerCase()}</Tag>,
                  }))}
                />
              </Form.Item>
            </Col>
            <Col xs={12} md={7}>
              <Form.Item
                name="status"
                label="Status"
                style={compact}
                rules={[{ required: true, message: 'Please select status' }]}
              >
                <Select>
                  {ticketStatuses.map(status => (
                    <Option key={status.id} value={status.id}>
                      <span
                        style={{
                          display: 'inline-block', width: 8, height: 8, borderRadius: '50%',
                          background: status.color, marginRight: 8,
                        }}
                      />
                      {status.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="description"
            label="Description"
            style={compact}
            rules={[{ required: true, message: 'Please enter ticket description' }]}
          >
            <TextArea
              autoSize={{ minRows: 3, maxRows: 14 }}
              placeholder="What happened, and what do you need?"
            />
          </Form.Item>

          <Form.Item style={compact}>
            <Upload
              multiple
              beforeUpload={handleUpload}
              fileList={attachments.map(a => ({
                uid: a.uid || a.id,
                name: a.name,
                status: 'done',
                size: a.size
              }))}
              onRemove={(file) => {
                removeAttachment(file.uid);
                return true;
              }}
            >
              <Button size="small" icon={<PaperClipOutlined />} loading={uploading}>
                Attach screenshots or files
              </Button>
            </Upload>
          </Form.Item>

          <Collapse
            size="small"
            ghost
            activeKey={openSections}
            onChange={(keys) => setOpenSections(Array.isArray(keys) ? keys : [keys])}
            items={moreSections}
            style={{ marginInline: -12 }}
          />
        </Form>
      </Card>
    </div>
  );
};

export default CreateTicket;
